import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PoseLearningAI: React.FC = () => {
  const navigate = useNavigate();
  const exampleRef = useRef<HTMLDivElement>(null);
  const practiceRef = useRef<HTMLDivElement>(null);
  const [showPdf, setShowPdf] = useState(false);

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      overflow: 'auto',
      padding: '1rem'
    }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
          <button
          onClick={() => navigate('/class-materials/ai-robot-project')}
            style={{
              marginRight: '1rem',
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ← 돌아가기
          </button>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>포즈학습 AI 모델 준비하기</h1>
        </div>
        
        <div style={{ marginBottom: '1rem', border: '1px solid #ccc', padding: '0.8rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontWeight: 'bold', marginBottom: '1rem', margin: '0 0 1rem 0' }}>
            📚 목차
          </h2>
        <ul style={{ listStyleType: 'none', padding: 0 }}>
          <li style={{marginBottom: '8px'}}>
              <a href="#AI 포즈 학습 개요" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
                AI 포즈 학습 개요
              </a>
            </li>
            <li style={{marginBottom: '8px'}}>
              <a href="#포즈 데이터 수집" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
                포즈 데이터 수집
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
              <a href="#AI 모델 훈련" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
                AI 모델 훈련
            </a>
          </li>
          <li style={{marginBottom: '8px'}}>
              <a href="#모델 테스트" style={{textDecoration: 'none', color: '#1976d2', fontWeight: 500}}>
                모델 테스트
            </a>
          </li>
        </ul>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'end', marginBottom: '1rem', gap: '1rem' }}>
          <p style={{ color: '#666', margin: 0 }}>
            본 섹션에서는 AI 모델을 활용한 포즈 인식 시스템을 구축합니다. 
            카메라를 통해 사용자의 포즈를 실시간으로 인식하고, 
            학습된 포즈 패턴에 따라 로봇을 제어하는 AI 모델을 준비하는 방법을 학습합니다.
          </p>
        </div>
        
        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem', margin: '1rem 0 0.5rem 0' }} id="AI 포즈 학습 개요">
          🤖 AI 포즈 학습 개요
        </h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          AI 포즈 학습은 컴퓨터 비전과 머신러닝 기술을 활용하여 사람의 동작과 자세를 인식하는 기술입니다.<br/>
          본 프로젝트에서는 다음과 같은 포즈들을 학습시켜 로봇 제어에 활용합니다:
        </p>
        
        <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#f0f8ff', marginBottom: '1rem' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#1976d2' }}>학습할 포즈 종류:</h4>
          <ul style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
            <li><strong>전진 포즈</strong>: 양팔을 앞으로 뻗은 자세</li>
            <li><strong>후진 포즈</strong>: 양팔을 뒤로 뻗은 자세</li>
            <li><strong>좌회전 포즈</strong>: 왼팔만 앞으로 뻗은 자세</li>
            <li><strong>우회전 포즈</strong>: 오른팔만 앞으로 뻗은 자세</li>
            <li><strong>정지 포즈</strong>: 양팔을 아래로 내린 자세</li>
          </ul>
        </div>
        
        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem', margin: '1rem 0 0.5rem 0' }} id="포즈 데이터 수집">
          📸 포즈 데이터 수집
        </h2>
        
        <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: 'rgba(255, 220, 175, 0.8)', marginBottom: '1rem' }}>
          <h3 style={{ fontWeight: 700, color: '#1976d2', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
            1. 포즈 데이터 수집 방법
          </h3>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            AI 모델이 정확하게 포즈를 인식할 수 있도록 충분한 양의 데이터를 수집해야 합니다.
          </p>
          <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#f5f5f5', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#1976d2' }}>데이터 수집 단계:</h4>
            <ol style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
              <li>카메라 권한 허용 및 위치 조정</li>
              <li>각 포즈별로 20-30장의 이미지 촬영</li>
              <li>다양한 각도와 거리에서 촬영</li>
              <li>조명 조건을 달리하여 촬영</li>
              <li>포즈별로 라벨링 (전진, 후진, 좌회전, 우회전, 정지)</li>
            </ol>
          </div>
          <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#fff3cd', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>⚠️ 주의사항:</h4>
            <ul style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
              <li>일관된 포즈를 유지하여 촬영하세요</li>
              <li>배경이 단순한 곳에서 촬영하세요</li>
              <li>충분한 조명이 있는 환경에서 촬영하세요</li>
              <li>각 포즈마다 충분한 양의 데이터를 수집하세요</li>
            </ul>
          </div>
        </div>

        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem', margin: '1rem 0 0.5rem 0' }} id="AI 모델 훈련">
          🧠 AI 모델 훈련
        </h2>
        
        <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: 'rgba(255, 220, 175, 0.8)', marginBottom: '1rem' }}>
          <h3 style={{ fontWeight: 700, color: '#1976d2', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
            2. AI 모델 훈련 과정
          </h3>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            수집된 포즈 데이터를 활용하여 AI 모델을 훈련시킵니다.
          </p>
          <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#e8f5e8', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#2e7d32' }}>훈련 과정:</h4>
            <ol style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
              <li>수집된 이미지 데이터를 훈련용과 테스트용으로 분할</li>
              <li>AI 모델 아키텍처 선택 (TensorFlow.js, Teachable Machine 등)</li>
              <li>모델 훈련 시작 및 진행 상황 모니터링</li>
              <li>훈련 완료 후 정확도 확인</li>
              <li>필요시 추가 데이터 수집 및 재훈련</li>
            </ol>
          </div>
          <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#f0f8ff', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#1976d2' }}>추천 도구:</h4>
            <ul style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
              <li><strong>Teachable Machine</strong>: 구글에서 제공하는 무료 AI 훈련 도구</li>
              <li><strong>TensorFlow.js</strong>: 웹 브라우저에서 실행 가능한 머신러닝 라이브러리</li>
              <li><strong>MediaPipe</strong>: 구글에서 제공하는 포즈 인식 API</li>
            </ul>
          </div>
        </div>

        <h2 style={{ fontWeight: 'bold', marginBottom: '0.5rem', margin: '1rem 0 0.5rem 0' }} id="모델 테스트">
          🧪 모델 테스트
        </h2>
        
        <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: 'rgba(255, 220, 175, 0.8)', marginBottom: '1rem' }}>
          <h3 style={{ fontWeight: 700, color: '#1976d2', marginBottom: '0.5rem', margin: '0 0 0.5rem 0' }}>
            3. 훈련된 모델 테스트
          </h3>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            훈련된 AI 모델이 정확하게 포즈를 인식하는지 테스트합니다.
          </p>
          <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#f5f5f5', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#1976d2' }}>테스트 방법:</h4>
            <ol style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
              <li>훈련된 모델을 웹 브라우저에서 로드</li>
              <li>실시간 카메라 입력으로 포즈 인식 테스트</li>
              <li>각 포즈별 인식 정확도 확인</li>
              <li>다양한 환경에서의 인식 성능 테스트</li>
              <li>인식 결과를 로봇 제어 명령으로 변환</li>
            </ol>
          </div>
          <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#fff3cd', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>성공 기준:</h4>
            <ul style={{ color: '#666', margin: 0, paddingLeft: '1.5rem' }}>
              <li>각 포즈별 인식 정확도 85% 이상</li>
              <li>실시간 인식 지연시간 1초 이내</li>
              <li>다양한 조명 조건에서 안정적인 인식</li>
              <li>로봇 제어 명령 정확한 전달</li>
            </ul>
          </div>
        </div>
        
        <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: '#e8f5e8', marginBottom: '1rem' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#2e7d32' }}>🎯 다음 단계:</h4>
          <p style={{ color: '#666', margin: 0 }}>
            AI 모델 훈련이 완료되면 <a href="/class-materials/Microbit/AIRobotControl" style={{color: '#1976d2', fontWeight: 500}}>AI 모델 연동 후 로봇 주행 테스트하기</a>로 
            진행하여 실제 로봇 제어를 구현해보세요.
          </p>
        </div>
      </div>
    );
  };

  export default PoseLearningAI;

